import { Component, Input, OnInit } from '@angular/core';
import { MountpointConnectionService, MountPointConnection, PagedResult } from '../../services/mountpoint-connection.service';
import { MatDialog } from '@angular/material/dialog';
import { AddMpcDialogComponent } from './add-mpc-dialog.component';
import { PermissionService } from '../../services/permission.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AcknowledgeDialogComponent } from '../acknowledge-dialog/acknowledge-dialog.component';

@Component({
  selector: 'app-external-mountpoint-connections',
  templateUrl: './external-mountpoint-connections.component.html',
  styleUrls: ['./external-mountpoint-connections.component.scss']
})
export class ExternalMountpointConnectionsComponent implements OnInit {
  title = 'Mountpoint Connection Setup';
  @Input() hideTitle = false;
  search = '';
  page = 0;
  size = 10;
  total = 0;
  rows: MountPointConnection[] = [];
  selected: MountPointConnection | null = null;
  editMode = false;
  selectedIds = new Set<number>();
  isDemo = false;

  get drawerOpen(): boolean {
    return !!this.selected;
  }

  constructor(
    private svc: MountpointConnectionService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private perms: PermissionService
  ) {
    this.perms.ensureFresh();
    this.isDemo = this.perms.has('demo');
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.svc.list(this.search, this.page, this.size).subscribe((res: PagedResult<MountPointConnection>) => {
      this.rows = res.content;
      this.total = res.totalElements;
      if (this.selected) {
        const ref = this.rows.find(r => r.mountPointId === this.selected!.mountPointId);
        if (ref) {
          this.selected = ref;
        }
      }
    });
  }

  onSearch(): void {
    this.page = 0;
    this.load();
  }

  onPageChange(p: number): void {
    this.page = p;
    this.load();
  }

  maskPassword(pw: string): string {
    if (!pw) return '';
    if (pw.length <= 4) return pw;
    return pw.substring(0, 2) + '***' + pw.substring(pw.length - 2);
  }

  isPortNumeric(value?: string | null): boolean {
    if (!value) return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    if (!/^\d+$/.test(trimmed)) return false;
    const num = Number(trimmed);
    return num >= 1 && num <= 65535;
  }

  toggleSelect(row: MountPointConnection, checked: boolean): void {
    if (checked) {
      this.selectedIds.add(row.mountPointId!);
    } else {
      this.selectedIds.delete(row.mountPointId!);
    }
  }

  selectRow(row: MountPointConnection): void {
    this.selected = { ...row };
    this.editMode = false;
  }

  enableEdit(): void {
    if (this.isDemo) {
      this.readonlyNotice();
      return;
    }
    if (this.selected) {
      this.editMode = true;
    }
  }

  cancelEdit(): void {
    this.editMode = false;
    if (this.selected) {
      this.selectRow(this.selected);
    }
  }

  onBackdropClick(): void {
    this.closeDetails();
  }

  closeDetails(): void {
    this.editMode = false;
    this.selected = null;
  }

  save(): void {
    if (this.isDemo) {
      this.readonlyNotice();
      return;
    }
    if (!this.selected) return;
    if (!this.selected.server || !this.selected.server.trim()) {
      this.snack.open('Server is required.', 'Close', { duration: 3000 });
      return;
    }
    if (!this.selected.port || !this.selected.port.trim()) {
      this.snack.open('Port is required.', 'Close', { duration: 3000 });
      return;
    }
    const trimmedPort = this.selected.port.trim();
    if (!this.isPortNumeric(trimmedPort)) {
      this.snack.open('Port must be numeric (1-65535).', 'Close', { duration: 3000 });
      return;
    }
    const req: MountPointConnection = {
      mountPointId: this.selected.mountPointId,
      userId: this.selected.userId,
      mountPoint: this.selected.mountPoint,
      server: this.selected.server.trim(),
      port: trimmedPort,
      downloaderUserName: this.selected.downloaderUserName,
      downloaderPassword: this.selected.downloaderPassword
    };
    this.selected.server = req.server;
    this.selected.port = req.port;
    this.svc.update(req).subscribe({
      next: () => {
        this.editMode = false;
        this.load();
      },
      error: () => {
        alert('Save failed (maybe duplicate mount_point for your account).');
      }
    });
  }

  add(): void {
    if (this.isDemo) {
      this.readonlyNotice();
      return;
    }
    this.svc.getCurrentUserId().subscribe(userId => {
      if (!userId) {
        this.snack.open('Cannot read user id from token', 'Close', { duration: 3000 });
        return;
      }
      const ref = this.dialog.open(AddMpcDialogComponent, {
        width: '520px',
        data: { user_id: userId }
      });
      ref.afterClosed().subscribe(form => {
        if (!form) return;
        const { mount_point, server, port, d_user_name, d_password } = form;
        if (!mount_point || !server || !port || !d_user_name || !d_password) {
          this.snack.open('All fields are required', 'Close', { duration: 3000 });
          return;
        }
        if (!this.isPortNumeric(port)) {
          this.snack.open('Port must be numeric and between 1 and 65535.', 'Close', { duration: 3000 });
          return;
        }
        const req: MountPointConnection = {
          userId,
          mountPoint: mount_point,
          server,
          port,
          downloaderUserName: d_user_name,
          downloaderPassword: d_password
        };
        this.svc.create(req).subscribe({
          next: () => {
            this.snack.open('Created', 'Close', { duration: 2000 });
            this.load();
          },
          error: err => {
            if (err?.status === 409) {
              this.snack.open('Duplicate: user_id + mount_point must be unique', 'Close', { duration: 4000 });
            } else {
              this.snack.open('Create failed', 'Close', { duration: 3000 });
            }
          }
        });
      });
    });
  }

  deleteSelected(): void {
    if (this.isDemo) {
      this.readonlyNotice();
      return;
    }
    if (this.selectedIds.size === 0) {
      alert('Please select item(s) first.');
      return;
    }
    if (!confirm(`Delete ${this.selectedIds.size} record(s)?`)) return;
    const ids = Array.from(this.selectedIds);
    this.svc.delete(ids).subscribe({
      next: () => {
        this.selectedIds.clear();
        this.selected = null;
        this.load();
      },
      error: () => alert('Delete failed.')
    });
  }

  private readonlyNotice(): void {
    this.dialog.open(AcknowledgeDialogComponent, { data: 'You have read-only access, but not permission to make changes.' });
  }
}


